import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, ExternalLink, AlertTriangle, TrendingUp, CircleDot } from 'lucide-react';
import { useState, useRef, useEffect, useMemo, useCallback, Fragment } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { Chart, registerables } from 'chart.js';
import type { RoadmapEpic, BurndownPoint, RoadmapInsightCategory, RoadmapResponse, Product, DeliveryTrackingStatus } from '@sprintpulse/shared';
import { api } from '../lib/api';
import { queryKeys } from '../lib/queryKeys';
import { RoadmapConfluenceReportModal } from '../components/RoadmapConfluenceReportModal';

Chart.register(...registerables);

function statusBadgeStyle(cat: RoadmapEpic['statusCategory']): React.CSSProperties {
  if (cat === 'done') return { background: 'var(--green-dim)', color: 'var(--green)' };
  if (cat === 'indeterminate') return { background: 'var(--accent-dim)', color: 'var(--accent)' };
  return { background: 'var(--bg-subtle)', color: 'var(--text-3)' };
}

const TRACKING_STYLES: Record<DeliveryTrackingStatus, { label: string; color: string; bg: string }> = {
  done:      { label: 'Done',      color: 'var(--green)', bg: 'var(--green-dim)' },
  on_track:  { label: 'On track',  color: 'var(--accent)', bg: 'var(--accent-dim)' },
  at_risk:   { label: 'At risk',   color: 'var(--amber)', bg: 'var(--amber-dim)' },
  breaching: { label: 'Breaching', color: 'var(--red)', bg: 'var(--red-dim)' },
};

function TrackingStatusBadge({ status }: { status: DeliveryTrackingStatus }): JSX.Element {
  const s = TRACKING_STYLES[status];
  return (
    <span
      className="inline-block px-1.5 py-0.5 rounded text-xs font-medium"
      style={{ color: s.color, background: s.bg }}
    >
      {s.label}
    </span>
  );
}

type TimelineGroupBy = 'productGroup' | 'theme' | 'client' | 'none';

function TimelineChart({ epics, hasDetails, groupBy, onGroupByChange, onShowTooltip, onHideTooltip }: {
  epics: RoadmapEpic[];
  hasDetails: boolean;
  groupBy: TimelineGroupBy;
  onGroupByChange: (v: TimelineGroupBy) => void;
  onShowTooltip: (e: React.MouseEvent, epic: RoadmapEpic) => void;
  onHideTooltip: () => void;
}): JSX.Element {
  const epicsWithTargets = epics.filter((e) => e.deliveryTarget);
  if (epicsWithTargets.length === 0) return <></>;

  const now = new Date();

  const allStarts = epicsWithTargets.map((e) => new Date(e.deliveryTarget!.start));
  const allEnds = epicsWithTargets.map((e) => new Date(e.deliveryTarget!.end));
  const minDate = new Date(Math.min(...allStarts.map((d) => d.getTime()), now.getTime()));
  const maxDate = new Date(Math.max(...allEnds.map((d) => d.getTime()), now.getTime()));

  // Padding
  minDate.setDate(minDate.getDate() - 7);
  maxDate.setDate(maxDate.getDate() + 14);

  const totalMs = maxDate.getTime() - minDate.getTime();
  const toPercent = (date: Date): number => ((date.getTime() - minDate.getTime()) / totalMs) * 100;
  const todayPct = toPercent(now);

  // Month labels
  const months: { label: string; leftPct: number }[] = [];
  const cursor = new Date(minDate);
  cursor.setUTCDate(1);
  cursor.setUTCMonth(cursor.getUTCMonth() + 1);
  while (cursor <= maxDate) {
    months.push({
      label: cursor.toLocaleDateString('en-GB', { month: 'short', year: '2-digit' }),
      leftPct: toPercent(cursor),
    });
    cursor.setUTCMonth(cursor.getUTCMonth() + 1);
  }

  return (
    <div
      className="rounded-lg border p-5 mb-6"
      style={{ background: 'var(--bg-panel)', borderColor: 'var(--border)' }}
    >
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-sm font-semibold" style={{ color: 'var(--text-1)' }}>
          Delivery Timeline
        </h2>
        <div className="flex items-center gap-2">
          <span className="text-xs" style={{ color: 'var(--text-3)' }}>Group by</span>
          <div className="relative">
            <select
              value={groupBy}
              onChange={(e) => onGroupByChange(e.target.value as TimelineGroupBy)}
              className="text-xs pl-3 pr-8 py-1.5 rounded-md border appearance-none font-medium"
              style={{
                background: 'var(--bg-panel)',
                borderColor: 'var(--border)',
                color: 'var(--text-1)',
                boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
                cursor: 'pointer',
              }}
            >
              <option value="productGroup">Product</option>
              <option value="theme">Theme</option>
              <option value="client">Impacted Client</option>
              <option value="none">None</option>
            </select>
            <div className="absolute inset-y-0 right-2 flex items-center pointer-events-none" style={{ color: 'var(--text-3)' }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
            </div>
          </div>
        </div>
      </div>

      {/* Month labels */}
      <div className="relative" style={{ height: '20px', marginBottom: '8px', marginLeft: '104px' }}>
        {months.map((m) => (
          <span
            key={m.label}
            className="absolute text-xs"
            style={{ left: `${m.leftPct}%`, color: 'var(--text-3)', transform: 'translateX(-50%)' }}
          >
            {m.label}
          </span>
        ))}
      </div>

      {/* Timeline rows */}
      <div className="relative">
        {(() => {
          // Group epics by selected field
          const grouped = new Map<string, RoadmapEpic[]>();
          if (groupBy === 'none') {
            grouped.set('__all__', epicsWithTargets);
          } else {
            for (const epic of epicsWithTargets) {
              const key = groupBy === 'theme'
                ? (epic.theme || 'No theme')
                : groupBy === 'client'
                ? (epic.impactedClient || 'No client')
                : groupBy === 'productGroup'
                ? (epic.productGroup || 'No product')
                : (epic.productGroup || 'No product');
              if (!grouped.has(key)) grouped.set(key, []);
              grouped.get(key)!.push(epic);
            }
          }

          return Array.from(grouped.entries()).map(([groupLabel, groupEpics]) => (
            <div key={groupLabel} className="mb-3">
              {/* Group header */}
              {groupBy !== 'none' && (() => {
                const groupColor = { color: 'var(--text-2)', bg: 'var(--bg-subtle)' };
                return (
                  <div className="flex items-center gap-3 mb-1" style={{ height: '22px' }}>
                    <div className="shrink-0 text-right" style={{ width: '92px' }}>
                      <span
                        className="inline-block px-2 py-0.5 rounded-full text-xs font-medium truncate max-w-full"
                        style={{ background: groupColor.bg, color: groupColor.color }}
                        title={groupLabel}
                      >
                        {groupLabel}
                      </span>
                    </div>
                    <div
                      className="flex-1 relative"
                      style={{ height: '1px', background: groupColor.color, opacity: 0.2 }}
                    />
                  </div>
                );
              })()}

              {/* Epic rows in this group */}
              {groupEpics.map((epic) => {
                const target = epic.deliveryTarget!;
                const startPct = toPercent(new Date(target.start));
                const endPct = toPercent(new Date(target.end));
                const widthPct = endPct - startPct;
                const status = epic.trackingStatus ?? 'on_track';
                const colors = TRACKING_STYLES[status];
                const progressWidth = hasDetails ? epic.percentDone : 0;

                return (
                  <div key={epic.key} className="flex items-center gap-3 mb-1.5" style={{ height: '24px' }}>
                    {/* Epic label */}
                    <div className="shrink-0 text-right" style={{ width: '92px' }}>
                      <a
                        href={epic.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs font-mono hover:underline"
                        style={{ color: 'var(--accent)' }}
                      >
                        {epic.key}
                      </a>
                    </div>

                    {/* Bar area */}
                    <div className="flex-1 relative" style={{ height: '18px' }}>
                      {/* Today line inside bar area */}
                      <div
                        className="absolute top-0 bottom-0"
                        style={{
                          left: `${todayPct}%`,
                          width: '1.5px',
                          background: 'var(--red)',
                          opacity: 0.35,
                          zIndex: 5,
                        }}
                      />
                      <div
                        className="absolute rounded cursor-default"
                        style={{
                          left: `${startPct}%`,
                          width: `${Math.max(widthPct, 0.5)}%`,
                          height: '100%',
                          background: colors.bg,
                          border: `1px solid ${colors.color}`,
                          overflow: 'hidden',
                        }}
                        onMouseEnter={(e) => hasDetails && onShowTooltip(e, epic)}
                        onMouseLeave={onHideTooltip}
                      >
                        {/* Progress fill */}
                        <div
                          className="h-full rounded-l"
                          style={{
                            width: `${progressWidth}%`,
                            background: colors.color,
                            opacity: 0.5,
                            transition: 'width 0.3s',
                          }}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ));
        })()}

        {/* Today label */}
        <div className="flex items-center gap-3 mt-1" style={{ height: '16px' }}>
          <div style={{ width: '92px' }} />
          <div className="flex-1 relative">
            <span
              className="absolute text-xs font-medium"
              style={{ left: `${todayPct}%`, color: 'var(--red)', transform: 'translateX(-50%)', fontSize: '9px' }}
            >
              Today
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

function BurndownChart({ points, title, quarterEnd }: { points: BurndownPoint[]; title: string; quarterEnd?: string }): JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const chartRef = useRef<Chart | null>(null);

  useEffect(() => {
    if (!canvasRef.current || points.length === 0) return;

    const style = getComputedStyle(document.documentElement);
    const accent = style.getPropertyValue('--accent').trim();
    const green = style.getPropertyValue('--green').trim();
    const red = style.getPropertyValue('--red').trim() || '#ef4444';
    const grid = style.getPropertyValue('--chart-grid').trim() || 'rgba(128,128,128,0.15)';
    const text2 = style.getPropertyValue('--text-2').trim();

    // Split into actual and forecast segments (overlap at the junction point)
    const lastActualIdx = points.findIndex((p) => p.forecast) - 1;
    const hasForecast = points.some((p) => p.forecast);

    // Actual data: null out forecast indices
    const actualRemaining = points.map((p, i) =>
      !p.forecast || (hasForecast && i === lastActualIdx + 1 && i === lastActualIdx + 1) ? p.remaining : null,
    );
    const actualDone = points.map((p) => (!p.forecast ? p.done : null));

    // Forecast data: null out actual indices except the junction point
    const forecastRemaining = points.map((p, i) =>
      p.forecast || i === lastActualIdx ? p.remaining : null,
    );
    const forecastDone = points.map((p, i) =>
      p.forecast || i === lastActualIdx ? p.done : null,
    );

    // Check if forecast shows items won't be completed by quarter end
    const lastForecast = points[points.length - 1];
    const willComplete = lastForecast && lastForecast.remaining === 0;
    const forecastColor = willComplete ? green : red;

    if (chartRef.current) chartRef.current.destroy();

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const datasets: any[] = [
      {
        label: 'Remaining',
        data: actualRemaining,
        borderColor: accent,
        backgroundColor: accent + '20',
        fill: true,
        tension: 0.3,
        pointRadius: 2,
        spanGaps: true,
      },
      {
        label: 'Done',
        data: actualDone,
        borderColor: green,
        backgroundColor: green + '20',
        fill: true,
        tension: 0.3,
        pointRadius: 2,
        spanGaps: true,
      },
    ];

    if (hasForecast) {
      datasets.push(
        {
          label: 'Forecast (remaining)',
          data: forecastRemaining,
          borderColor: forecastColor,
          borderDash: [6, 3],
          backgroundColor: 'transparent',
          fill: false,
          tension: 0.3,
          pointRadius: 2,
          pointStyle: 'triangle',
          spanGaps: true,
        },
        {
          label: 'Forecast (done)',
          data: forecastDone,
          borderColor: green,
          borderDash: [6, 3],
          backgroundColor: 'transparent',
          fill: false,
          tension: 0.3,
          pointRadius: 2,
          pointStyle: 'triangle',
          spanGaps: true,
        },
      );
    }

    chartRef.current = new Chart(canvasRef.current, {
      type: 'line',
      data: {
        labels: points.map((p) => p.date),
        datasets,
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { position: 'top', labels: { color: text2, boxWidth: 12, font: { size: 11 } } },
          title: { display: true, text: title, color: text2, font: { size: 13, weight: 'normal' } },
        },
        scales: {
          x: { ticks: { color: text2, font: { size: 10 }, maxRotation: 45 }, grid: { color: grid } },
          y: { beginAtZero: true, ticks: { color: text2, font: { size: 10 } }, grid: { color: grid } },
        },
      },
    });

    return () => { chartRef.current?.destroy(); };
  }, [points, title, quarterEnd]);

  if (points.length === 0) return <></>;

  return (
    <div style={{ position: 'relative', height: '280px', width: '100%' }}>
      <canvas ref={canvasRef} />
    </div>
  );
}

interface Insight {
  type: 'danger' | 'warning' | 'info' | 'success';
  title: string;
  detail: string;
  epics?: { key: string; summary: string; url: string; stat: string }[];
}

function computeInsights(epics: RoadmapEpic[], burndown: BurndownPoint[], quarterLabel: string): Insight[] {
  const insights: Insight[] = [];
  if (epics.length === 0) return insights;

  const totalItems = epics.reduce((s, e) => s + e.totalIssues, 0);
  const doneItems = epics.reduce((s, e) => s + e.doneIssues, 0);
  const totalBlocked = epics.reduce((s, e) => s + e.blockedIssues, 0);
  const remaining = totalItems - doneItems;

  // 1. Forecast gap — will items finish by quarter end?
  const forecastPoints = burndown.filter((p) => p.forecast);
  if (forecastPoints.length > 0) {
    const lastForecast = forecastPoints[forecastPoints.length - 1];
    if (lastForecast.remaining > 0) {
      insights.push({
        type: 'danger',
        title: `${lastForecast.remaining} items projected to miss ${quarterLabel}`,
        detail: `At current velocity, ${lastForecast.remaining} of ${totalItems} items will remain incomplete by quarter end. ${remaining} items are currently outstanding.`,
      });
    } else {
      insights.push({
        type: 'success',
        title: `On track to complete all items by ${quarterLabel}`,
        detail: `Current velocity projects all ${totalItems} items will be done before quarter end.`,
      });
    }
  }

  // 2. Velocity — items done per week this quarter
  const actuals = burndown.filter((p) => !p.forecast);
  if (actuals.length >= 2) {
    const first = actuals[0];
    const last = actuals[actuals.length - 1];
    const doneInQuarter = last.done - first.done;
    const msElapsed = new Date(last.date).getTime() - new Date(first.date).getTime();
    const weeksElapsed = Math.max(1, msElapsed / (7 * 24 * 60 * 60 * 1000));
    const velocity = doneInQuarter / weeksElapsed;

    if (velocity > 0 && remaining > 0) {
      const weeksNeeded = remaining / velocity;
      insights.push({
        type: 'info',
        title: `Velocity: ${velocity.toFixed(1)} items/week`,
        detail: `${doneInQuarter} items completed over ${weeksElapsed.toFixed(1)} weeks this quarter. At this pace, ${remaining} remaining items need ~${Math.ceil(weeksNeeded)} more weeks.`,
      });
    }
  }

  // 3. Blocked bottlenecks
  if (totalBlocked > 0) {
    const blockedEpics = epics
      .filter((e) => e.blockedIssues > 0)
      .sort((a, b) => b.blockedIssues - a.blockedIssues);
    insights.push({
      type: 'danger',
      title: `${totalBlocked} blocked items across ${blockedEpics.length} epics`,
      detail: 'Blocked items prevent progress and impact downstream work. Prioritise unblocking these to maintain velocity.',
      epics: blockedEpics.map((e) => ({
        key: e.key,
        summary: e.summary,
        url: e.url,
        stat: `${e.blockedIssues} blocked`,
      })),
    });
  }

  // 4. Zero-progress epics (not started)
  const zeroProgress = epics.filter((e) => e.percentDone === 0 && e.totalIssues > 0);
  if (zeroProgress.length > 0) {
    const totalZeroItems = zeroProgress.reduce((s, e) => s + e.totalIssues, 0);
    insights.push({
      type: 'warning',
      title: `${zeroProgress.length} epics have not started (${totalZeroItems} items)`,
      detail: 'These epics have 0% completion. If they are committed for this quarter, they represent unstarted risk.',
      epics: zeroProgress.map((e) => ({
        key: e.key,
        summary: e.summary,
        url: e.url,
        stat: `${e.totalIssues} items, 0% done`,
      })),
    });
  }

  // 5. At-risk epics (low completion, high remaining — excluding zero-progress to avoid duplication)
  const atRisk = epics
    .filter((e) => e.percentDone > 0 && e.percentDone < 50 && e.totalIssues - e.doneIssues >= 3)
    .sort((a, b) => (b.totalIssues - b.doneIssues) - (a.totalIssues - a.doneIssues));
  if (atRisk.length > 0) {
    insights.push({
      type: 'warning',
      title: `${atRisk.length} epics at risk (<50% done, 3+ remaining)`,
      detail: 'These epics have significant remaining work with low completion. Consider re-prioritising or adding capacity.',
      epics: atRisk.map((e) => ({
        key: e.key,
        summary: e.summary,
        url: e.url,
        stat: `${e.percentDone}% done, ${e.totalIssues - e.doneIssues} remaining`,
      })),
    });
  }

  // 6. Concentration risk — single epic with disproportionate remaining work
  if (remaining > 0) {
    const topEpic = epics
      .filter((e) => e.totalIssues - e.doneIssues > 0)
      .sort((a, b) => (b.totalIssues - b.doneIssues) - (a.totalIssues - a.doneIssues))[0];
    if (topEpic) {
      const topRemaining = topEpic.totalIssues - topEpic.doneIssues;
      const pctOfTotal = Math.round((topRemaining / remaining) * 100);
      if (pctOfTotal >= 30) {
        insights.push({
          type: 'info',
          title: `${topEpic.key} holds ${pctOfTotal}% of remaining work`,
          detail: `"${topEpic.summary}" has ${topRemaining} of ${remaining} remaining items. Progress on this epic significantly impacts the overall burndown.`,
        });
      }
    }
  }

  // 7. Breaching delivery targets
  const breaching = epics.filter((e) => e.trackingStatus === 'breaching');
  if (breaching.length > 0) {
    insights.push({
      type: 'danger',
      title: `${breaching.length} epic${breaching.length > 1 ? 's' : ''} breaching delivery target`,
      detail: 'These epics have passed their target end date or are severely behind schedule. Immediate action required.',
      epics: breaching.map((e) => ({
        key: e.key, summary: e.summary, url: e.url,
        stat: `${e.percentDone}% done, target: ${e.deliveryTarget?.end ?? 'N/A'}`,
      })),
    });
  }

  // 8. At-risk delivery targets
  const atRiskTarget = epics.filter((e) => e.trackingStatus === 'at_risk');
  if (atRiskTarget.length > 0) {
    insights.push({
      type: 'warning',
      title: `${atRiskTarget.length} epic${atRiskTarget.length > 1 ? 's' : ''} at risk of missing target`,
      detail: 'Progress is falling behind the delivery timeline. These may not complete by their target end date.',
      epics: atRiskTarget.map((e) => ({
        key: e.key, summary: e.summary, url: e.url,
        stat: `${e.percentDone}% done, target: ${e.deliveryTarget?.end ?? 'N/A'}`,
      })),
    });
  }

  return insights;
}

function InsightsPanel({ insights }: { insights: Insight[] }): JSX.Element {
  if (insights.length === 0) return <></>;

  const iconMap = {
    danger: <AlertCircle size={14} />,
    warning: <AlertTriangle size={14} />,
    info: <TrendingUp size={14} />,
    success: <CircleDot size={14} />,
  };
  const colorMap = {
    danger: 'var(--red)',
    warning: 'var(--yellow, #f59e0b)',
    info: 'var(--accent)',
    success: 'var(--green)',
  };
  const bgMap = {
    danger: 'var(--red-dim, rgba(239,68,68,0.1))',
    warning: 'var(--yellow-dim, rgba(245,158,11,0.1))',
    info: 'var(--accent-dim, rgba(99,102,241,0.1))',
    success: 'var(--green-dim, rgba(34,197,94,0.1))',
  };

  return (
    <div
      className="rounded-lg border p-5 mb-6"
      style={{ background: 'var(--bg-panel)', borderColor: 'var(--border)' }}
    >
      <h2 className="text-sm font-semibold mb-4" style={{ color: 'var(--text-1)' }}>
        Insights & Bottlenecks
      </h2>
      <div className="space-y-3">
        {insights.map((insight, i) => (
          <div
            key={i}
            className="rounded-md px-4 py-3"
            style={{ background: bgMap[insight.type], borderLeft: `3px solid ${colorMap[insight.type]}` }}
          >
            <div className="flex items-center gap-2 mb-1" style={{ color: colorMap[insight.type] }}>
              {iconMap[insight.type]}
              <span className="text-sm font-medium">{insight.title}</span>
            </div>
            <p className="text-xs ml-5" style={{ color: 'var(--text-2)' }}>
              {insight.detail}
            </p>
            {insight.epics && insight.epics.length > 0 && (
              <div className="ml-5 mt-2 space-y-1">
                {insight.epics.map((e) => (
                  <div key={e.key} className="flex items-center gap-2 text-xs">
                    <a
                      href={e.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-mono hover:underline"
                      style={{ color: 'var(--accent)' }}
                    >
                      {e.key}
                    </a>
                    <span style={{ color: 'var(--text-2)' }} className="truncate flex-1">
                      {e.summary}
                    </span>
                    <span className="font-mono shrink-0" style={{ color: colorMap[insight.type] }}>
                      {e.stat}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

const AI_STATUS_STYLES: Record<RoadmapInsightCategory['status'], { icon: string; color: string; bg: string; border: string }> = {
  positive:   { icon: '↑', color: 'var(--green)',  bg: 'var(--green-dim)',  border: 'var(--green)' },
  neutral:    { icon: '→', color: 'var(--text-2)', bg: 'var(--bg-panel)',   border: 'var(--border)' },
  concerning: { icon: '↓', color: 'var(--red)',    bg: 'var(--red-dim)',    border: 'var(--red)' },
};

const AI_CATEGORY_ORDER: RoadmapInsightCategory['category'][] = [
  'overall', 'delivery_risk', 'bottlenecks', 'capacity', 'recommendations',
];

function AIInsightCard({ category }: { category: RoadmapInsightCategory }): JSX.Element {
  const isActions = category.category === 'recommendations';
  const style = isActions
    ? { icon: '✓', color: 'var(--accent)', bg: 'var(--accent-dim)', border: 'var(--accent)' }
    : AI_STATUS_STYLES[category.status];

  return (
    <div
      className={`rounded-lg p-4 border${isActions ? ' sm:col-span-2' : ''}`}
      style={{ background: style.bg, borderColor: style.border }}
    >
      <div className="flex items-center gap-2 mb-2">
        <span
          className="text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center shrink-0"
          style={{ background: style.color, color: '#fff' }}
        >
          {style.icon}
        </span>
        <h4 className="text-sm font-semibold" style={{ color: 'var(--text-1)' }}>
          {category.title}
        </h4>
      </div>
      <p className="text-xs mb-3 leading-relaxed" style={{ color: 'var(--text-2)' }}>
        {category.summary}
      </p>
      {isActions ? (
        <ol className="space-y-2">
          {category.highlights.map((h, i) => (
            <li key={i} className="flex gap-3 text-xs">
              <span
                className="shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold"
                style={{ background: 'var(--accent)', color: '#fff', marginTop: '1px' }}
              >
                {i + 1}
              </span>
              <span style={{ color: 'var(--text-1)' }}>{h}</span>
            </li>
          ))}
        </ol>
      ) : (
        <ul className="space-y-1">
          {category.highlights.map((h, i) => (
            <li key={i} className="flex gap-2 text-xs" style={{ color: 'var(--text-2)' }}>
              <span className="shrink-0 mt-0.5" style={{ color: style.color }}>•</span>
              <span>{h}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function AIInsightsPanel({ roadmapData, productId }: { roadmapData: RoadmapResponse | undefined; productId?: string }): JSX.Element {
  const [open, setOpen] = useState(false);

  const { data, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: queryKeys.roadmapInsights(productId),
    queryFn: () => api.getRoadmapInsights(roadmapData!),
    enabled: open && !!roadmapData,
    staleTime: 10 * 60 * 1000,
    retry: false,
  });

  const sorted = data?.categories.slice().sort(
    (a, b) => AI_CATEGORY_ORDER.indexOf(a.category) - AI_CATEGORY_ORDER.indexOf(b.category),
  );
  const cards = sorted?.filter((c) => c.category !== 'recommendations');
  const actions = sorted?.find((c) => c.category === 'recommendations');

  return (
    <div
      className="rounded-lg border mb-6"
      style={{ background: 'var(--bg-panel)', borderColor: 'var(--border)' }}
    >
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-5 py-4 text-left"
      >
        <div className="flex items-center gap-3">
          <div
            className="w-6 h-6 rounded flex items-center justify-center shrink-0"
            style={{ background: 'var(--accent)', color: '#fff' }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 2L10.5 7.5L16 9L10.5 10.5L9 16L7.5 10.5L2 9L7.5 7.5Z"/>
              <path d="M18 12L19 15L22 16L19 17L18 20L17 17L14 16L17 15Z"/>
            </svg>
          </div>
          <div>
            <p className="text-sm font-semibold" style={{ color: 'var(--text-1)' }}>
              Roadmap Delivery Insights
            </p>
            <p className="text-xs" style={{ color: 'var(--text-3)' }}>
              {open && data
                ? `Generated ${new Date(data.generatedAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`
                : 'Click to generate AI analysis of delivery health'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {open && data && (
            <button
              onClick={(e) => { e.stopPropagation(); refetch(); }}
              className="text-xs px-2 py-1 rounded border"
              style={{ color: 'var(--text-3)', borderColor: 'var(--border)' }}
              title="Regenerate insights"
            >
              ↻ Refresh
            </button>
          )}
          <span
            className="text-xs font-medium"
            style={{
              color: 'var(--accent)',
              transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
              display: 'inline-block',
              transition: 'transform 0.2s',
            }}
          >
            ▾
          </span>
        </div>
      </button>

      {open && (
        <div className="px-5 pb-5 border-t" style={{ borderColor: 'var(--border)' }}>
          {(isLoading || isFetching) && (
            <div className="flex items-center gap-3 py-8 justify-center">
              <div
                className="w-4 h-4 rounded-full border-2 animate-spin"
                style={{ borderColor: 'var(--accent)', borderTopColor: 'transparent' }}
              />
              <span className="text-sm" style={{ color: 'var(--text-3)' }}>
                Analysing delivery data with AI…
              </span>
            </div>
          )}

          {isError && !isFetching && (
            <div className="py-6 text-center">
              <p className="text-sm" style={{ color: 'var(--red)' }}>
                Failed to generate AI insights. Check backend logs for details.
              </p>
              <button
                onClick={() => refetch()}
                className="mt-3 text-xs px-3 py-1.5 rounded border"
                style={{ color: 'var(--text-2)', borderColor: 'var(--border)' }}
              >
                Try again
              </button>
            </div>
          )}

          {sorted && !isFetching && (
            <div className="pt-4 space-y-4">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {cards?.map((cat) => (
                  <AIInsightCard key={cat.category} category={cat} />
                ))}
              </div>
              {actions && (
                <div className="grid grid-cols-1">
                  <AIInsightCard category={actions} />
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** Redirect /roadmap to the first product that has a roadmap configured */
export function RoadmapRedirect(): JSX.Element {
  const { data: products = [], isLoading } = useQuery({
    queryKey: queryKeys.products(),
    queryFn: api.getProducts,
  });
  const first = products.find((p: Product) => p.jiraRoadmapProjectKey);
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="w-5 h-5 rounded-full border-2 border-t-transparent animate-spin"
          style={{ borderColor: 'var(--border-mid)', borderTopColor: 'var(--accent)' }} />
      </div>
    );
  }
  if (first) return <Navigate to={`/roadmap/${first.id}`} replace />;
  return (
    <div className="p-8 text-center py-20">
      <p className="text-sm" style={{ color: 'var(--text-3)' }}>
        No products have a Jira roadmap project configured.
      </p>
      <p className="text-xs mt-2" style={{ color: 'var(--text-3)' }}>
        Add a roadmap project key to a product in Admin to enable this view.
      </p>
    </div>
  );
}

export function RoadmapPage(): JSX.Element {
  const { productId: activeProductId } = useParams<{ productId: string }>();
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [timelineGroupBy, setTimelineGroupBy] = useState<TimelineGroupBy>('productGroup');
  const [tableGroupBy, setTableGroupBy] = useState<TimelineGroupBy>('productGroup');
  const [tooltip, setTooltip] = useState<{ epic: RoadmapEpic; x: number; y: number } | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [showRoadmapReport, setShowRoadmapReport] = useState(false);
  const queryClient = useQueryClient();

  const showTooltip = useCallback((e: React.MouseEvent, epic: RoadmapEpic) => {
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setTooltip({ epic, x: rect.left + rect.width / 2, y: rect.top });
  }, []);
  const hideTooltip = useCallback(() => setTooltip(null), []);

  // Fetch products for the header product name
  const { data: products = [] } = useQuery({
    queryKey: queryKeys.products(),
    queryFn: api.getProducts,
  });

  const roadmapProducts = products.filter((p: Product) => p.jiraRoadmapProjectKey);

  const handleRefresh = useCallback(async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      // Remove cached data entirely — useQuery will immediately re-fetch
      queryClient.removeQueries({ queryKey: queryKeys.roadmapInsights(activeProductId) });
      queryClient.removeQueries({ queryKey: queryKeys.roadmapDetails(activeProductId) });
      queryClient.removeQueries({ queryKey: queryKeys.roadmap(activeProductId) });
    } finally {
      setRefreshing(false);
    }
  }, [activeProductId, queryClient, refreshing]);

  // Fast query: epics with names/categories/status (~300ms)
  const { data: quickData, isLoading: quickLoading, error: quickError } = useQuery({
    queryKey: queryKeys.roadmap(activeProductId),
    queryFn: () => api.getRoadmap(activeProductId),
    enabled: !!activeProductId,
    refetchInterval: 5 * 60 * 1000,
  });

  // Full query: epics with progress + burndowns (~1-1.5s, concurrent child fetch)
  const { data: fullData, isLoading: detailsLoading } = useQuery({
    queryKey: queryKeys.roadmapDetails(activeProductId),
    queryFn: () => api.getRoadmapDetails(activeProductId),
    enabled: !!activeProductId,
    refetchInterval: 5 * 60 * 1000,
  });

  // Prefer full data when available, fall back to quick data
  const data = fullData ?? quickData;
  const hasDetails = !!fullData;

  const epics = data?.epics ?? [];
  const productGroups = data?.productGroups ?? [];
  const burndownAll = data?.burndown ?? [];
  const productBurndowns = data?.productBurndowns ?? {};
  const quarterLabel = data?.quarterLabel ?? '';
  const quarterEnd = data?.quarterEnd;

  // Product groups for the filter dropdown
  const allFilterOptions = [...productGroups].sort();

  const filtered = selectedCategory === 'all'
    ? epics
    : epics.filter((e) =>
        e.productGroup.split(', ').includes(selectedCategory)
      );

  const burndown = selectedCategory === 'all'
    ? burndownAll
    : (productBurndowns[selectedCategory] ?? []);

  const totalItems = filtered.reduce((s, e) => s + e.totalIssues, 0);
  const doneItems = filtered.reduce((s, e) => s + e.doneIssues, 0);
  const overallPercent = totalItems > 0 ? Math.round((doneItems / totalItems) * 100) : 0;

  const insights = useMemo(
    () => computeInsights(filtered, burndown, quarterLabel),
    [filtered, burndown, quarterLabel],
  );

  if (quickLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div
          className="w-5 h-5 rounded-full border-2 border-t-transparent animate-spin"
          style={{ borderColor: 'var(--border-mid)', borderTopColor: 'var(--accent)' }}
        />
      </div>
    );
  }

  if (quickError) {
    return (
      <div className="p-8 flex items-center gap-2 text-sm" style={{ color: 'var(--red)' }}>
        <AlertCircle size={14} /> Failed to load roadmap
      </div>
    );
  }

  return (
    <div className="p-8 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-start justify-between mb-8 gap-4">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold tracking-tight" style={{ color: 'var(--text-1)' }}>
            {products.find((p: Product) => p.id === activeProductId)?.name ?? data?.project ?? ''}{quarterLabel ? ` - ${quarterLabel} Roadmap` : ' Roadmap'}
          </h1>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {/* Refresh */}
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md font-medium transition-all"
            style={{
              background: refreshing ? 'var(--border)' : '#0F5132',
              color: '#ffffff',
              border: '1px solid transparent',
              opacity: refreshing ? 0.7 : 1,
              boxShadow: refreshing ? 'none' : '0 1px 2px rgba(0,0,0,0.15)',
              cursor: refreshing ? 'not-allowed' : 'pointer',
            }}
            title="Refresh roadmap data from Jira"
          >
            <span
              style={{
                display: 'inline-block',
                fontSize: '13px',
                animation: refreshing ? 'spin 1s linear infinite' : 'none',
              }}
            >
              ↻
            </span>
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </button>
          {/* Roadmap Report */}
          <button
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md font-medium transition-all"
            style={{
              background: '#1D4ED8',
              color: '#ffffff',
              border: '1px solid transparent',
              boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
              cursor: 'pointer',
            }}
            title="Generate roadmap report in Confluence"
            onClick={() => setShowRoadmapReport(true)}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14,2 14,8 20,8"/>
              <line x1="16" y1="13" x2="8" y2="13"/>
              <line x1="16" y1="17" x2="8" y2="17"/>
            </svg>
            Roadmap Report
          </button>
          {/* Category / Product filter */}
          {allFilterOptions.length > 0 && (
            <div className="relative">
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="text-xs pl-3 pr-8 py-1.5 rounded-md border appearance-none font-medium"
                style={{
                  background: 'var(--bg-panel)',
                  borderColor: 'var(--border)',
                  color: 'var(--text-1)',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
                  cursor: 'pointer',
                }}
              >
                <option value="all">All</option>
                {productGroups.map((p) => <option key={`pg-${p}`} value={p}>{p}</option>)}
              </select>
              <div className="absolute inset-y-0 right-2 flex items-center pointer-events-none" style={{ color: 'var(--text-3)' }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        {[
          { label: 'Total items', value: hasDetails ? totalItems : null },
          { label: 'Done', value: hasDetails ? `${doneItems} (${overallPercent}%)` : null },
          { label: 'Remaining', value: hasDetails ? `${totalItems - doneItems} (${totalItems > 0 ? 100 - overallPercent : 0}%)` : null },
        ].map((card) => (
          <div
            key={card.label}
            className="rounded-lg border p-4"
            style={{ background: 'var(--bg-panel)', borderColor: 'var(--border)' }}
          >
            <div className="text-xs uppercase tracking-wider mb-1" style={{ color: 'var(--text-3)' }}>
              {card.label}
            </div>
            <div className="text-xl font-semibold" style={{ color: 'var(--text-1)' }}>
              {card.value ?? (
                <span
                  className="inline-block w-12 h-5 rounded animate-pulse"
                  style={{ background: 'var(--bg-subtle)' }}
                />
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Burndown chart */}
      {burndown.length > 1 ? (
        <div
          className="rounded-lg border p-5 mb-6"
          style={{ background: 'var(--bg-panel)', borderColor: 'var(--border)' }}
        >
          <BurndownChart
            points={burndown}
            title={`${quarterLabel} Burndown - ${selectedCategory === 'all' ? 'All Items' : selectedCategory}`}
            quarterEnd={quarterEnd}
          />
        </div>
      ) : detailsLoading ? (
        <div
          className="rounded-lg border p-5 mb-6 flex items-center justify-center"
          style={{ background: 'var(--bg-panel)', borderColor: 'var(--border)', height: '280px' }}
        >
          <div className="flex items-center gap-3">
            <div
              className="w-4 h-4 rounded-full border-2 animate-spin"
              style={{ borderColor: 'var(--accent)', borderTopColor: 'transparent' }}
            />
            <span className="text-xs" style={{ color: 'var(--text-3)' }}>Loading burndown…</span>
          </div>
        </div>
      ) : null}

      {/* Insights & Bottlenecks */}
      <InsightsPanel insights={insights} />

      {/* Roadmap Delivery Insights */}
      <AIInsightsPanel roadmapData={fullData} productId={activeProductId} />

      {/* Delivery Timeline */}
      <TimelineChart epics={filtered} hasDetails={hasDetails} groupBy={timelineGroupBy} onGroupByChange={setTimelineGroupBy} onShowTooltip={showTooltip} onHideTooltip={hideTooltip} />

      {/* Epics table */}
      {filtered.length === 0 ? (
        <div
          className="border-2 border-dashed rounded-lg py-20 text-center"
          style={{ borderColor: 'var(--border)' }}
        >
          <p className="text-sm" style={{ color: 'var(--text-3)' }}>
            No items found{selectedCategory !== 'all' ? ` for ${selectedCategory}` : ''}.
          </p>
        </div>
      ) : (() => {
        // Group epics for the table
        const tableGroups = new Map<string, RoadmapEpic[]>();
        if (tableGroupBy === 'none') {
          tableGroups.set('__all__', filtered);
        } else {
          for (const epic of filtered) {
            const key = tableGroupBy === 'theme'
              ? (epic.theme || 'No theme')
              : tableGroupBy === 'client'
              ? (epic.impactedClient || 'No client')
              : tableGroupBy === 'productGroup'
              ? (epic.productGroup || 'No product')
              : (epic.productGroup || 'No product');
            if (!tableGroups.has(key)) tableGroups.set(key, []);
            tableGroups.get(key)!.push(epic);
          }
        }
        const colCount = tableGroupBy !== 'none' ? 4 : 5;

        return (
          <div
            className="rounded-lg border overflow-hidden"
            style={{ background: 'var(--bg-panel)', borderColor: 'var(--border)' }}
          >
            {/* Table header with group-by dropdown */}
            <div
              className="flex items-center justify-between px-4 py-2"
              style={{ borderBottom: '1px solid var(--border)' }}
            >
              <span className="text-xs font-semibold" style={{ color: 'var(--text-1)' }}>
                Epics ({filtered.length})
              </span>
              <div className="flex items-center gap-2">
                <span className="text-xs" style={{ color: 'var(--text-3)' }}>Group by</span>
                <div className="relative">
                  <select
                    value={tableGroupBy}
                    onChange={(e) => setTableGroupBy(e.target.value as TimelineGroupBy)}
                    className="text-xs pl-3 pr-8 py-1 rounded-md border appearance-none font-medium"
                    style={{
                      background: 'var(--bg-panel)',
                      borderColor: 'var(--border)',
                      color: 'var(--text-1)',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
                      cursor: 'pointer',
                    }}
                  >
                    <option value="category">Category</option>
                    <option value="productGroup">Product</option>
                    <option value="theme">Theme</option>
                    <option value="client">Impacted Client</option>
                    <option value="none">None</option>
                  </select>
                  <div className="absolute inset-y-0 right-2 flex items-center pointer-events-none" style={{ color: 'var(--text-3)' }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
                  </div>
                </div>
              </div>
            </div>

            <table className="w-full text-sm">
              <thead>
                <tr
                  className="text-xs uppercase tracking-wider"
                  style={{ color: 'var(--text-3)', borderBottom: '1px solid var(--border)' }}
                >
                  <th className="text-left px-4 py-3 font-medium">Epic</th>
                  {tableGroupBy === 'none' && <th className="text-left px-4 py-3 font-medium">Product</th>}
                  <th className="text-left px-4 py-3 font-medium">Status</th>
                  <th className="text-left px-4 py-3 font-medium">Target</th>
                  <th className="text-left px-4 py-3 font-medium">Progress</th>
                </tr>
              </thead>
              <tbody>
                {Array.from(tableGroups.entries()).map(([groupLabel, groupEpics]) => {
                  const groupColor = { color: 'var(--text-2)', bg: 'var(--bg-subtle)' };

                  return (
                    <Fragment key={groupLabel}>
                      {/* Group header row */}
                      {tableGroupBy !== 'none' && (
                        <tr>
                          <td
                            colSpan={colCount}
                            className="px-4 py-2"
                            style={{ background: groupColor.bg, borderBottom: '1px solid var(--border)' }}
                          >
                            <span className="text-xs font-semibold" style={{ color: groupColor.color }}>
                              {groupLabel}
                            </span>
                            <span className="text-xs ml-2" style={{ color: 'var(--text-3)' }}>
                              ({groupEpics.length})
                            </span>
                          </td>
                        </tr>
                      )}
                      {/* Epic rows */}
                      {groupEpics.map((epic) => (
                        <tr
                          key={epic.key}
                          className="transition-colors"
                          style={{ borderBottom: '1px solid var(--border)' }}
                        >
                          <td className="px-4 py-3">
                            <div className="flex items-start gap-2">
                              <a
                                href={epic.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="font-mono text-xs shrink-0 hover:underline"
                                style={{ color: 'var(--accent)' }}
                              >
                                {epic.key}
                                <ExternalLink size={10} className="inline ml-1 -mt-0.5" />
                              </a>
                              <span style={{ color: 'var(--text-1)' }}>{epic.summary}</span>
                            </div>
                          </td>

                          {tableGroupBy === 'none' && (
                            <td className="px-4 py-3">
                              {epic.productGroup ? (
                                <span
                                  className="inline-block px-2 py-0.5 rounded-full text-xs font-medium"
                                  style={{ background: 'var(--bg-subtle)', color: 'var(--text-2)' }}
                                >
                                  {epic.productGroup}
                                </span>
                              ) : (
                                <span className="text-xs" style={{ color: 'var(--text-3)' }}>—</span>
                              )}
                            </td>
                          )}

                          <td className="px-4 py-3">
                            <span
                              className="inline-block px-2 py-0.5 rounded text-xs font-medium"
                              style={statusBadgeStyle(epic.statusCategory)}
                            >
                              {epic.status}
                            </span>
                          </td>

                          <td className="px-4 py-3">
                            {epic.deliveryTarget ? (
                              <div className="flex items-center gap-2">
                                <span className="text-xs font-mono" style={{ color: 'var(--text-2)' }}>
                                  {new Date(epic.deliveryTarget.end).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                                </span>
                                {hasDetails && epic.trackingStatus && (
                                  <TrackingStatusBadge status={epic.trackingStatus} />
                                )}
                              </div>
                            ) : (
                              <span className="text-xs" style={{ color: 'var(--text-3)' }}>—</span>
                            )}
                          </td>

                          <td className="px-4 py-3 w-44">
                            {hasDetails ? (
                              <div
                                className="flex items-center gap-2 cursor-default"
                                onMouseEnter={(e) => showTooltip(e, epic)}
                                onMouseLeave={hideTooltip}
                              >
                                <div
                                  className="flex-1 h-2 rounded-full overflow-hidden"
                                  style={{ background: 'var(--bg-subtle)' }}
                                >
                                  <div
                                    className="h-full rounded-full transition-all"
                                    style={{
                                      width: `${epic.percentDone}%`,
                                      background: epic.percentDone === 100 ? 'var(--green)' : 'var(--accent)',
                                    }}
                                  />
                                </div>
                                <span className="text-xs font-medium w-9 text-right" style={{ color: 'var(--text-2)' }}>
                                  {epic.percentDone}%
                                </span>
                              </div>
                            ) : (
                              <div
                                className="h-2 rounded-full animate-pulse"
                                style={{ background: 'var(--bg-subtle)' }}
                              />
                            )}
                          </td>
                        </tr>
                      ))}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        );
      })()}

      {/* Roadmap Confluence Report Modal */}
      {showRoadmapReport && fullData && (
        <RoadmapConfluenceReportModal
          roadmapData={fullData}
          productName={roadmapProducts.find((p: Product) => p.id === activeProductId)?.name ?? fullData.project}
          quarterLabel={quarterLabel}
          onClose={() => setShowRoadmapReport(false)}
        />
      )}

      {/* Fixed-position progress tooltip */}
      {tooltip && (
        <div
          className="fixed pointer-events-none"
          style={{
            left: tooltip.x,
            top: tooltip.y,
            transform: 'translate(-50%, -100%)',
            marginTop: -8,
            zIndex: 9999,
          }}
        >
          <div
            style={{
              background: 'var(--bg-panel)',
              border: '1px solid var(--border)',
              borderRadius: 6,
              padding: '8px 12px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              whiteSpace: 'nowrap',
            }}
          >
            <div className="text-xs font-semibold mb-1.5" style={{ color: 'var(--text-1)' }}>
              {tooltip.epic.key} - {tooltip.epic.percentDone}% complete
            </div>
            <div className="flex flex-col gap-1 text-xs" style={{ color: 'var(--text-2)' }}>
              <div className="flex items-center gap-1.5">
                <span className="inline-block w-2 h-2 rounded-full shrink-0" style={{ background: 'var(--green)' }} />
                Done: {tooltip.epic.doneIssues} / {tooltip.epic.totalIssues}
              </div>
              {tooltip.epic.blockedIssues > 0 && (
                <div className="flex items-center gap-1.5">
                  <span className="inline-block w-2 h-2 rounded-full shrink-0" style={{ background: 'var(--red)' }} />
                  Blocked: {tooltip.epic.blockedIssues}
                </div>
              )}
              <div className="flex items-center gap-1.5">
                <span className="inline-block w-2 h-2 rounded-full shrink-0" style={{ background: 'var(--text-3)' }} />
                Pending: {tooltip.epic.totalIssues - tooltip.epic.doneIssues}
              </div>
            </div>
          </div>
          {/* Arrow */}
          <div
            className="mx-auto w-0 h-0"
            style={{
              borderLeft: '6px solid transparent',
              borderRight: '6px solid transparent',
              borderTop: '6px solid var(--border)',
            }}
          />
        </div>
      )}
    </div>
  );
}
